import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { projectPermission } from '../../utils/permissions';
import {
  fetchExecutivePlan,
  createPhase,
  updatePhase,
  deletePhase,
  addStoryToPhase,
  removeStoryFromPhase,
  searchStories,
  clearPlan,
  clearStorySearch,
} from '../../store/slices/executivePlanSlice';
import './ExecutivePlan.css';

// ── Constants ──────────────────────────────────────────────────────────────

const ZOOM_LEVELS = [
  { key: 'weeks', label: 'Weeks', pxPerDay: 18 },
  { key: 'months', label: 'Months', pxPerDay: 7 },
  { key: 'quarters', label: 'Quarters', pxPerDay: 3 },
];

const PHASE_COLORS = [
  '#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626',
  '#0891b2', '#4f46e5', '#0d9488', '#c026d3', '#ea580c',
];

const STATUS_LABELS = {
  'on-track': 'On Track',
  'at-risk': 'At Risk',
  'behind': 'Behind',
  'complete': 'Complete',
  'not-started': 'Not Started',
};

// ── Date utilities ─────────────────────────────────────────────────────────

const parseDate = (str) => {
  if (!str) return new Date();
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatDateShort = (str) => {
  if (!str) return '—';
  const d = parseDate(str);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
};

const formatDateFull = (str) => {
  if (!str) return '—';
  const d = parseDate(str);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const daysBetween = (a, b) => {
  const d1 = typeof a === 'string' ? parseDate(a) : a;
  const d2 = typeof b === 'string' ? parseDate(b) : b;
  return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
};

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

const getPhaseStatus = (phase) => {
  if (phase.percent_complete >= 100) return 'complete';
  if (phase.percent_complete === 0) return 'not-started';
  const today = new Date();
  const end = parseDate(phase.end_date);
  const start = parseDate(phase.start_date);
  const totalDays = daysBetween(start, end) || 1;
  const elapsed = daysBetween(start, today);
  const expected = Math.min(100, Math.max(0, (elapsed / totalDays) * 100));
  if (phase.percent_complete >= expected - 10) return 'on-track';
  if (phase.percent_complete >= expected - 25) return 'at-risk';
  return 'behind';
};

const calcAutoPercent = (stories) => {
  if (!stories || stories.length === 0) return 0;
  const totalPoints = stories.reduce((s, st) => s + (st.story_points || 1), 0);
  const donePoints = stories.filter(st => st.status === 'done').reduce((s, st) => s + (st.story_points || 1), 0);
  const ipPoints = stories.filter(st => st.status === 'in_progress').reduce((s, st) => s + (st.story_points || 1), 0);
  return Math.round((donePoints + ipPoints * 0.5) / totalPoints * 100);
};

// ── Phase Modal ────────────────────────────────────────────────────────────

const PhaseModal = ({ isOpen, onClose, onSave, phase, allPhases, members, saving }) => {
  const [form, setForm] = useState({
    name: '',
    phase_type: 'phase',
    start_date: '',
    end_date: '',
    owner_id: '',
    color: PHASE_COLORS[0],
    percent_complete: 0,
    manual_override: false,
    dependencies: [],
  });

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: phase?.name || '',
        phase_type: phase?.phase_type || 'phase',
        start_date: phase?.start_date || '',
        end_date: phase?.end_date || '',
        owner_id: phase?.owner?.id || '',
        color: phase?.color || PHASE_COLORS[0],
        percent_complete: phase?.percent_complete ?? 0,
        manual_override: phase?.manual_override || false,
        dependencies: phase?.dependencies || [],
      });
    }
  }, [isOpen, phase]);

  if (!isOpen) return null;

  const isEdit = !!phase;

  const handleSave = () => {
    if (!form.name.trim() || !form.start_date) return;
    onSave({
      ...form,
      owner_id: form.owner_id ? parseInt(form.owner_id) : null,
      end_date: form.phase_type === 'milestone' ? form.start_date : form.end_date,
    });
  };

  const toggleDep = (id) => {
    setForm(prev => ({
      ...prev,
      dependencies: prev.dependencies.includes(id)
        ? prev.dependencies.filter(d => d !== id)
        : [...prev.dependencies, id],
    }));
  };

  return (
    <div className="ep-modal-overlay" onClick={onClose}>
      <div className="ep-modal" onClick={e => e.stopPropagation()}>
        <div className="ep-modal-header">
          <h2 className="ep-modal-title">{isEdit ? 'Edit Phase' : 'Add Phase'}</h2>
          <button className="ep-modal-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"/>
            </svg>
          </button>
        </div>
        <div className="ep-modal-body">
          <div className="ep-form-group">
            <label className="ep-form-label">Name</label>
            <input
              className="ep-form-input"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Discovery & Requirements"
              autoFocus
            />
          </div>
          <div className="ep-form-row">
            <div className="ep-form-group">
              <label className="ep-form-label">Type</label>
              <select
                className="ep-form-input"
                value={form.phase_type}
                onChange={e => setForm({ ...form, phase_type: e.target.value })}
                style={{ cursor: 'pointer' }}
              >
                <option value="phase">Phase</option>
                <option value="milestone">Milestone</option>
              </select>
            </div>
            <div className="ep-form-group">
              <label className="ep-form-label">Owner</label>
              <select
                className="ep-form-input"
                value={form.owner_id}
                onChange={e => setForm({ ...form, owner_id: e.target.value })}
                style={{ cursor: 'pointer' }}
              >
                <option value="">— None —</option>
                {(members || []).map(m => (
                  <option key={m.user.id} value={m.user.id}>
                    {m.user.first_name} {m.user.last_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="ep-form-row">
            <div className="ep-form-group">
              <label className="ep-form-label">{form.phase_type === 'milestone' ? 'Date' : 'Start Date'}</label>
              <input
                type="date"
                className="ep-form-input"
                value={form.start_date}
                onChange={e => setForm({ ...form, start_date: e.target.value })}
              />
            </div>
            {form.phase_type !== 'milestone' && (
              <div className="ep-form-group">
                <label className="ep-form-label">End Date</label>
                <input
                  type="date"
                  className="ep-form-input"
                  value={form.end_date}
                  onChange={e => setForm({ ...form, end_date: e.target.value })}
                />
              </div>
            )}
          </div>
          <div className="ep-form-group">
            <label className="ep-form-label">Color</label>
            <div className="ep-color-options">
              {PHASE_COLORS.map(c => (
                <button
                  key={c}
                  className={`ep-color-swatch ${form.color === c ? 'selected' : ''}`}
                  style={{ background: c }}
                  onClick={() => setForm({ ...form, color: c })}
                  type="button"
                />
              ))}
            </div>
          </div>
          {form.phase_type === 'milestone' ? (
            <div className="ep-form-group">
              <label className="ep-form-label">Status</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', padding: 'var(--space-2) var(--space-3)', background: form.percent_complete >= 100 ? '#d1fae5' : 'var(--color-bg-sunken)', border: `1px solid ${form.percent_complete >= 100 ? '#10b981' : 'var(--color-border)'}`, borderRadius: 'var(--radius-md)', transition: 'all 120ms ease' }}>
                <input
                  type="checkbox"
                  checked={form.percent_complete >= 100}
                  onChange={e => setForm({ ...form, percent_complete: e.target.checked ? 100 : 0, manual_override: true })}
                  style={{ width: 16, height: 16, accentColor: '#10b981' }}
                />
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)', color: form.percent_complete >= 100 ? '#065f46' : 'var(--color-text-secondary)' }}>
                  {form.percent_complete >= 100 ? 'Achieved ✓' : 'Mark as achieved'}
                </span>
              </label>
            </div>
          ) : (
            <div className="ep-form-group">
              <label className="ep-form-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                % Complete
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', fontWeight: 400, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.manual_override}
                    onChange={e => setForm({ ...form, manual_override: e.target.checked })}
                    style={{ width: 14, height: 14 }}
                  />
                  Manual override
                </label>
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={form.percent_complete}
                onChange={e => setForm({ ...form, percent_complete: parseInt(e.target.value), manual_override: true })}
                style={{ width: '100%', accentColor: 'var(--color-primary)' }}
              />
              <div style={{ textAlign: 'center', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                {form.percent_complete}%
              </div>
            </div>
          )}
          {allPhases && allPhases.length > 0 && (
            <div className="ep-form-group">
              <label className="ep-form-label">Dependencies (depends on)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {allPhases
                  .filter(p => !phase || p.id !== phase.id)
                  .map(p => (
                    <label
                      key={p.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontSize: 'var(--text-sm)', cursor: 'pointer', padding: '4px 8px',
                        background: form.dependencies.includes(p.id) ? 'var(--color-primary-subtle, rgba(37,99,235,0.08))' : 'var(--color-bg-sunken)',
                        border: `1px solid ${form.dependencies.includes(p.id) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                        borderRadius: 'var(--radius-sm)',
                        transition: 'all 120ms ease',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.dependencies.includes(p.id)}
                        onChange={() => toggleDep(p.id)}
                        style={{ width: 14, height: 14 }}
                      />
                      <span style={{ color: form.dependencies.includes(p.id) ? 'var(--color-primary)' : 'var(--color-text-secondary)' }}>
                        {p.name}
                      </span>
                    </label>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div className="ep-modal-footer">
          <button className="ep-btn ep-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="ep-btn ep-btn-primary"
            onClick={handleSave}
            disabled={!form.name.trim() || !form.start_date || saving}
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Phase'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Stories Modal ──────────────────────────────────────────────────────────

const StoriesModal = ({ isOpen, onClose, phase, projectId, onAdd, onRemove, searchResults, searchLoading }) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen && projectId) {
      dispatch(searchStories({ projectId, query: '' }));
    }
    if (!isOpen) {
      setSearch('');
      dispatch(clearStorySearch());
    }
  }, [isOpen, projectId, dispatch]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      dispatch(searchStories({ projectId, query: search }));
    }, 250);
    return () => clearTimeout(timer);
  }, [search, isOpen, projectId, dispatch]); // eslint-disable-line

  if (!isOpen || !phase) return null;

  const linkedIds = new Set(phase.story_ids || []);

  return (
    <div className="ep-modal-overlay" onClick={onClose}>
      <div className="ep-modal wide" onClick={e => e.stopPropagation()}>
        <div className="ep-modal-header">
          <h2 className="ep-modal-title">Link Stories — {phase.name}</h2>
          <button className="ep-modal-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"/>
            </svg>
          </button>
        </div>
        <div className="ep-modal-body">
          {/* Currently linked */}
          {(phase.stories || []).length > 0 && (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 'var(--space-2)' }}>
                Currently Linked ({phase.stories.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                {phase.stories.map(story => (
                  <div key={story.id} style={{ display: 'flex', alignItems: 'center', padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--color-border-light)', fontSize: 'var(--text-sm)' }}>
                    <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-primary)', width: 72, flexShrink: 0 }}>
                      {story.task_key}
                    </span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{story.title}</span>
                    <button
                      className="ep-btn ep-btn-ghost ep-btn-sm"
                      style={{ color: 'var(--color-danger, #ef4444)', flexShrink: 0 }}
                      onClick={() => onRemove(phase.id, story.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 'var(--space-2)' }}>
            Add Stories
          </div>
          <div className="ep-story-search">
            <svg className="ep-story-search-icon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M11.742 10.344a6.5 6.5 0 10-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 001.415-1.414l-3.85-3.85a1.007 1.007 0 00-.115-.1zM12 6.5a5.5 5.5 0 11-11 0 5.5 5.5 0 0111 0z"/>
            </svg>
            <input
              className="ep-story-search-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by key (e.g. PROJ-15) or title…"
              autoFocus
            />
          </div>

          <div className="ep-story-results">
            {searchLoading && (
              <div style={{ padding: 'var(--space-3)', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>
                Searching…
              </div>
            )}
            {!searchLoading && searchResults.map(story => {
              const isLinked = linkedIds.has(story.id);
              return (
                <button
                  key={story.id}
                  className={`ep-story-result ${isLinked ? 'added' : ''}`}
                  onClick={() => { if (!isLinked) onAdd(phase.id, story.id); }}
                  disabled={isLinked}
                >
                  <span className="ep-story-result-key">{story.task_key}</span>
                  <span className="ep-story-result-title">{story.title}</span>
                  {isLinked
                    ? <span className="ep-story-result-added">✓ Linked</span>
                    : <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)', fontWeight: 600, marginLeft: 'var(--space-2)', flexShrink: 0 }}>+ Add</span>
                  }
                </button>
              );
            })}
            {!searchLoading && searchResults.length === 0 && (
              <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>
                {search ? `No stories found for "${search}"` : 'No stories in this project yet'}
              </div>
            )}
          </div>
        </div>
        <div className="ep-modal-footer">
          <button className="ep-btn ep-btn-secondary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

// ── Summary Stats ──────────────────────────────────────────────────────────

const SummaryStats = ({ phases }) => {
  const allItems = phases || [];
  const phaseItems = allItems.filter(p => p.phase_type === 'phase');
  const total = allItems.length;
  const onTrack = phaseItems.filter(p => ['on-track', 'complete'].includes(getPhaseStatus(p))).length;
  const atRisk = phaseItems.filter(p => getPhaseStatus(p) === 'at-risk').length;
  const behind = phaseItems.filter(p => getPhaseStatus(p) === 'behind').length;

  return (
    <div className="ep-stats">
      <div className="ep-stat">
        <div className="ep-stat-icon brand">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
          </svg>
        </div>
        <div>
          <div className="ep-stat-value">{total}</div>
          <div className="ep-stat-label">Phases & Milestones</div>
        </div>
      </div>
      <div className="ep-stat">
        <div className="ep-stat-icon success">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
          </svg>
        </div>
        <div>
          <div className="ep-stat-value">{onTrack}</div>
          <div className="ep-stat-label">On Track</div>
        </div>
      </div>
      <div className="ep-stat">
        <div className="ep-stat-icon warning">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
          </svg>
        </div>
        <div>
          <div className="ep-stat-value">{atRisk}</div>
          <div className="ep-stat-label">At Risk</div>
        </div>
      </div>
      <div className="ep-stat">
        <div className="ep-stat-icon danger">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
          </svg>
        </div>
        <div>
          <div className="ep-stat-value">{behind}</div>
          <div className="ep-stat-label">Behind</div>
        </div>
      </div>
    </div>
  );
};

// ── Plan Table ─────────────────────────────────────────────────────────────

const PlanTable = ({ phases, expandedPhases, onToggleExpand, selectedId, onSelect, onEdit }) => (
  <div className="ep-split-left">
    <div className="ep-table-header">
      <div className="ep-th ep-th-name" style={{ paddingLeft: 32 }}>Phase / Milestone</div>
      <div className="ep-th ep-th-dates">Start</div>
      <div className="ep-th ep-th-dates">End</div>
      <div className="ep-th ep-th-progress">Progress</div>
      <div className="ep-th ep-th-status">Status</div>
    </div>
    <div className="ep-table-body" id="ep-table-scroll">
      {(phases || []).map(phase => (
        <PlanTableRow
          key={phase.id}
          phase={phase}
          expanded={!!expandedPhases[phase.id]}
          onToggle={() => onToggleExpand(phase.id)}
          selected={selectedId === phase.id}
          onSelect={() => onSelect(phase.id)}
          onEdit={() => onEdit(phase)}
        />
      ))}
      {(!phases || phases.length === 0) && (
        <div className="ep-empty">
          <svg viewBox="0 0 48 48" fill="currentColor">
            <path d="M6 14a4 4 0 014-4h8l4 4h12a4 4 0 014 4v16a4 4 0 01-4 4H10a4 4 0 01-4-4V14z"/>
          </svg>
          <p>No phases yet. Add your first phase to get started.</p>
        </div>
      )}
    </div>
  </div>
);

const PlanTableRow = ({ phase, expanded, onToggle, selected, onSelect, onEdit }) => {
  const stories = phase.stories || [];
  const hasStories = stories.length > 0;
  const status = getPhaseStatus(phase);
  const isMilestone = phase.phase_type === 'milestone';
  const progressColor = phase.percent_complete >= 100 ? 'var(--color-success, #10b981)'
    : phase.percent_complete > 0 ? phase.color
    : 'var(--color-bg-active)';

  return (
    <>
      <div
        className={`ep-row${selected ? ' selected' : ''}${isMilestone ? ' milestone' : ''}`}
        onClick={onSelect}
        onDoubleClick={onEdit}
      >
        <button
          className={`ep-row-expand${expanded ? ' expanded' : ''}${!hasStories ? ' placeholder' : ''}`}
          onClick={e => { e.stopPropagation(); if (hasStories) onToggle(); }}
          type="button"
        >
          <svg viewBox="0 0 16 16" fill="currentColor"><path d="M6 3.5l4.5 4.5L6 12.5V3.5z"/></svg>
        </button>
        <div className="ep-row-color" style={{ background: phase.color }} />
        {isMilestone && (
          <div className="ep-row-icon" style={{ background: phase.color, color: 'white' }}>
            <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1L10.5 7h-5L8 1zm0 14L5.5 9h5L8 15z"/>
            </svg>
          </div>
        )}
        <div className="ep-row-name">
          {phase.name}
          {hasStories && <span className="story-count">({stories.length})</span>}
        </div>
        <div className="ep-row-date">{formatDateShort(phase.start_date)}</div>
        <div className="ep-row-date">{isMilestone ? '—' : formatDateShort(phase.end_date)}</div>
        <div className="ep-row-progress">
          {!isMilestone && (
            <>
              <div className="ep-progress-bar">
                <div className="ep-progress-fill" style={{ width: `${phase.percent_complete}%`, background: progressColor }} />
              </div>
              <span className="ep-progress-text">{phase.percent_complete}%</span>
            </>
          )}
        </div>
        <div className="ep-row-status">
          <span className={`ep-status-badge ${status}`}>{STATUS_LABELS[status]}</span>
        </div>
      </div>
      {expanded && hasStories && stories.map(story => (
        <div key={story.id} className="ep-story-row">
          <span className="ep-story-key">{story.task_key}</span>
          <span className="ep-story-title">{story.title}</span>
          <span className={`ep-story-status ${story.status === 'done' ? 'done' : story.status === 'in_progress' ? 'in-progress' : 'todo'}`}>
            {story.status === 'done' ? 'Done' : story.status === 'in_progress' ? 'In Progress' : 'To Do'}
          </span>
          {story.story_points != null && (
            <span className="ep-story-points">{story.story_points} pts</span>
          )}
        </div>
      ))}
    </>
  );
};

// ── Gantt Chart ────────────────────────────────────────────────────────────

const GanttChart = ({ phases, expandedPhases, selectedId, onSelect, onEdit, zoom, showDeps }) => {
  const ganttBodyRef = useRef(null);
  const ganttHeaderRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const scrollSyncSource = useRef(null);

  const zoomConfig = ZOOM_LEVELS.find(z => z.key === zoom) || ZOOM_LEVELS[1];
  const pxPerDay = zoomConfig.pxPerDay;
  const rowHeight = 48;

  const allDates = (phases || []).flatMap(p => [p.start_date, p.end_date]).filter(Boolean);
  const sortedDates = [...allDates].sort();
  const rangeStart = allDates.length
    ? addDays(parseDate(sortedDates[0]), -7)
    : addDays(new Date(), -30);
  const rangeEnd = allDates.length
    ? addDays(parseDate(sortedDates[sortedDates.length - 1]), 14)
    : addDays(new Date(), 90);
  const totalDays = daysBetween(rangeStart, rangeEnd);
  const totalWidth = totalDays * pxPerDay;

  // Sync vertical scroll with table
  useEffect(() => {
    const tableEl = document.getElementById('ep-table-scroll');
    const ganttEl = ganttBodyRef.current;
    if (!tableEl || !ganttEl) return;
    const syncFromTable = () => {
      if (scrollSyncSource.current === 'gantt') return;
      scrollSyncSource.current = 'table';
      ganttEl.scrollTop = tableEl.scrollTop;
      requestAnimationFrame(() => { scrollSyncSource.current = null; });
    };
    const syncFromGantt = () => {
      if (scrollSyncSource.current === 'table') return;
      scrollSyncSource.current = 'gantt';
      tableEl.scrollTop = ganttEl.scrollTop;
      requestAnimationFrame(() => { scrollSyncSource.current = null; });
    };
    tableEl.addEventListener('scroll', syncFromTable);
    ganttEl.addEventListener('scroll', syncFromGantt);
    return () => {
      tableEl.removeEventListener('scroll', syncFromTable);
      ganttEl.removeEventListener('scroll', syncFromGantt);
    };
  }, []);

  // Sync horizontal scroll for header
  useEffect(() => {
    const ganttEl = ganttBodyRef.current;
    const headerEl = ganttHeaderRef.current;
    if (!ganttEl || !headerEl) return;
    const syncH = () => { headerEl.scrollLeft = ganttEl.scrollLeft; };
    ganttEl.addEventListener('scroll', syncH);
    return () => ganttEl.removeEventListener('scroll', syncH);
  }, []);

  // Auto-scroll to today
  useEffect(() => {
    const ganttEl = ganttBodyRef.current;
    if (!ganttEl) return;
    const today = new Date();
    const dayOffset = daysBetween(rangeStart, today);
    const todayX = dayOffset * pxPerDay;
    ganttEl.scrollLeft = Math.max(0, todayX - ganttEl.clientWidth / 3);
  }, [zoom]); // eslint-disable-line

  const dateToX = (dateStr) => daysBetween(rangeStart, parseDate(dateStr)) * pxPerDay;

  const renderTimelineHeader = () => {
    const months = [];
    const days = [];
    const cursor = new Date(rangeStart);

    while (cursor <= rangeEnd) {
      const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      const startDay = Math.max(0, daysBetween(rangeStart, monthStart));
      const endDay = Math.min(totalDays, daysBetween(rangeStart, monthEnd) + 1);
      const width = (endDay - startDay) * pxPerDay;
      const mNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      if (width > 0) {
        months.push(
          <div key={`m-${cursor.getMonth()}-${cursor.getFullYear()}`} className="ep-gantt-month" style={{ width, minWidth: width }}>
            {width > 60 ? `${mNames[cursor.getMonth()]} ${cursor.getFullYear()}` : mNames[cursor.getMonth()].substring(0, 3)}
          </div>
        );
      }
      cursor.setMonth(cursor.getMonth() + 1);
      cursor.setDate(1);
    }

    if (zoom === 'weeks') {
      const dayCursor = new Date(rangeStart);
      for (let i = 0; i < totalDays; i++) {
        const isWknd = isWeekend(dayCursor);
        const isToday = toDateStr(dayCursor) === toDateStr(new Date());
        days.push(
          <div key={`d-${i}`} className={`ep-gantt-day${isWknd ? ' weekend' : ''}${isToday ? ' today-mark' : ''}`} style={{ width: pxPerDay, minWidth: pxPerDay }}>
            {pxPerDay >= 16 ? dayCursor.getDate() : (i % 7 === 0 ? dayCursor.getDate() : '')}
          </div>
        );
        dayCursor.setDate(dayCursor.getDate() + 1);
      }
    } else {
      const dayCursor = new Date(rangeStart);
      for (let i = 0; i < totalDays; i++) {
        const isMonday = dayCursor.getDay() === 1;
        const isFirst = dayCursor.getDate() === 1;
        if (zoom === 'months' && (isMonday || isFirst)) {
          days.push(
            <div key={`d-${i}`} className="ep-gantt-day" style={{ width: pxPerDay * 7, minWidth: pxPerDay * 7 }}>
              {dayCursor.getDate()}
            </div>
          );
          dayCursor.setDate(dayCursor.getDate() + 7);
          i += 6;
        } else if (zoom === 'quarters' && isFirst) {
          const mNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const daysInMonth = new Date(dayCursor.getFullYear(), dayCursor.getMonth() + 1, 0).getDate();
          days.push(
            <div key={`d-${i}`} className="ep-gantt-day" style={{ width: daysInMonth * pxPerDay, minWidth: daysInMonth * pxPerDay }}>
              {mNames[dayCursor.getMonth()]}
            </div>
          );
          dayCursor.setDate(dayCursor.getDate() + daysInMonth);
          i += daysInMonth - 1;
        } else {
          dayCursor.setDate(dayCursor.getDate() + 1);
        }
      }
    }

    return (
      <div className="ep-gantt-header-row" ref={ganttHeaderRef} style={{ overflowX: 'hidden' }}>
        <div className="ep-gantt-months" style={{ width: totalWidth, minWidth: totalWidth }}>{months}</div>
        <div className="ep-gantt-days" style={{ width: totalWidth, minWidth: totalWidth }}>{days}</div>
      </div>
    );
  };

  const renderGridlines = () => {
    const lines = [];
    const cursor = new Date(rangeStart);
    for (let i = 0; i < totalDays; i++) {
      const isFirst = cursor.getDate() === 1;
      const isMonday = cursor.getDay() === 1;
      if (isFirst) {
        lines.push(<div key={`gl-${i}`} className="ep-gantt-gridline month-line" style={{ left: i * pxPerDay }} />);
      } else if (isMonday && zoom !== 'quarters') {
        lines.push(<div key={`gl-${i}`} className="ep-gantt-gridline" style={{ left: i * pxPerDay }} />);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return lines;
  };

  const renderTodayLine = () => {
    const today = new Date();
    const dayOffset = daysBetween(rangeStart, today);
    if (dayOffset < 0 || dayOffset > totalDays) return null;
    return <div className="ep-gantt-today-line" style={{ left: dayOffset * pxPerDay }} />;
  };

  const renderDependencies = (totalHeight) => {
    if (!showDeps) return null;
    const arrows = [];
    const positions = {};
    let yPos = 0;
    (phases || []).forEach(phase => {
      positions[phase.id] = {
        y: yPos + rowHeight / 2,
        startX: dateToX(phase.start_date),
        endX: phase.phase_type === 'milestone' ? dateToX(phase.start_date) + 8 : dateToX(phase.end_date),
      };
      yPos += rowHeight;
      if (expandedPhases[phase.id]) {
        yPos += (phase.stories || []).length * 36;
      }
    });
    (phases || []).forEach(phase => {
      (phase.dependencies || []).forEach(depId => {
        const from = positions[depId];
        const to = positions[phase.id];
        if (!from || !to) return;
        const midX = from.endX + 16;
        const path = `M${from.endX},${from.y} L${midX},${from.y} L${midX},${to.y} L${to.startX},${to.y}`;
        const arrowPath = `M${to.startX},${to.y} L${to.startX - 6},${to.y - 4} L${to.startX - 6},${to.y + 4} Z`;
        arrows.push(
          <g key={`dep-${depId}-${phase.id}`}>
            <path d={path} className="ep-dep-path"/>
            <path d={arrowPath} className="ep-dep-arrow"/>
          </g>
        );
      });
    });
    return (
      <svg className="ep-gantt-deps-svg" style={{ width: totalWidth, height: totalHeight }}>{arrows}</svg>
    );
  };

  const showTooltip = (e, phase) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      phase,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
  };

  const renderRows = () => {
    const rows = [];
    let yPos = 0;
    (phases || []).forEach(phase => {
      const stories = phase.stories || [];
      const isExpanded = !!expandedPhases[phase.id];
      const isMilestone = phase.phase_type === 'milestone';
      const startX = dateToX(phase.start_date);
      const endX = isMilestone ? startX : dateToX(phase.end_date);
      const barWidth = Math.max(endX - startX, 8);

      rows.push(
        <div key={`row-${phase.id}`} className="ep-gantt-row" style={{ height: rowHeight }}>
          {isMilestone ? (
            <div
              className="ep-gantt-milestone"
              style={{ left: startX - 8, background: phase.color }}
              onMouseEnter={e => showTooltip(e, phase)}
              onMouseLeave={() => setTooltip(null)}
              onClick={() => onSelect(phase.id)}
              onDoubleClick={() => onEdit(phase)}
            />
          ) : (
            <div
              className="ep-gantt-bar"
              style={{ left: startX, width: barWidth }}
              onMouseEnter={e => showTooltip(e, phase)}
              onMouseLeave={() => setTooltip(null)}
              onClick={() => onSelect(phase.id)}
              onDoubleClick={() => onEdit(phase)}
            >
              <div className="ep-gantt-bar-bg" style={{ background: phase.color }} />
              <div className="ep-gantt-bar-fill" style={{ width: `${phase.percent_complete}%`, background: phase.color }} />
              {barWidth > 60 && <span className="ep-gantt-bar-label">{phase.name}</span>}
            </div>
          )}
        </div>
      );
      yPos += rowHeight;

      if (isExpanded && stories.length > 0) {
        stories.forEach(story => {
          rows.push(<div key={`srow-${story.id}`} className="ep-gantt-story-row" />);
          yPos += 36;
        });
      }
    });
    return { rows, totalHeight: yPos };
  };

  const { rows, totalHeight } = renderRows();

  return (
    <div className="ep-split-right">
      {renderTimelineHeader()}
      <div className="ep-gantt-body" ref={ganttBodyRef}>
        <div className="ep-gantt-scroll-inner" style={{ width: totalWidth, minHeight: totalHeight }}>
          <div className="ep-gantt-gridlines">{renderGridlines()}</div>
          {renderTodayLine()}
          {renderDependencies(totalHeight)}
          {rows}
        </div>
      </div>
      {tooltip && (
        <div className="ep-tooltip" style={{
          left: Math.min(tooltip.x, window.innerWidth - 290),
          top: tooltip.y,
          transform: 'translate(-50%, -100%)',
        }}>
          <div className="ep-tooltip-title">{tooltip.phase.name}</div>
          <div className="ep-tooltip-row">
            <span>Dates</span>
            <span>{formatDateShort(tooltip.phase.start_date)} — {tooltip.phase.phase_type === 'milestone' ? 'Milestone' : formatDateShort(tooltip.phase.end_date)}</span>
          </div>
          <div className="ep-tooltip-row">
            <span>Progress</span>
            <span>{tooltip.phase.percent_complete}%{tooltip.phase.manual_override ? ' (manual)' : ''}</span>
          </div>
          {tooltip.phase.owner && (
            <div className="ep-tooltip-row">
              <span>Owner</span>
              <span>{tooltip.phase.owner.first_name} {tooltip.phase.owner.last_name}</span>
            </div>
          )}
          <div className="ep-tooltip-row">
            <span>Status</span>
            <span>{STATUS_LABELS[getPhaseStatus(tooltip.phase)]}</span>
          </div>
          {(tooltip.phase.stories || []).length > 0 && (
            <div className="ep-tooltip-row">
              <span>Stories</span>
              <span>
                {tooltip.phase.stories.filter(s => s.status === 'done').length}/{tooltip.phase.stories.length} done
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Executive Summary ──────────────────────────────────────────────────────

// Compute logical sequential display dates for Executive Summary.
// Phases with dependencies are shown starting only after their dependency's display end.
// Duration is preserved. Phases without dependencies use their actual dates.
const computeLogicalDates = (phases) => {
  const phaseMap = {};
  phases.forEach(p => { phaseMap[p.id] = p; });

  const display = {};
  const computing = new Set(); // cycle guard

  const getDisplay = (id) => {
    if (display[id]) return display[id];
    if (computing.has(id)) {
      // Circular dependency — fall back to actual dates
      const p = phaseMap[id];
      if (!p) return null;
      display[id] = { start: parseDate(p.start_date), end: parseDate(p.end_date || p.start_date) };
      return display[id];
    }
    const phase = phaseMap[id];
    if (!phase) return null;

    computing.add(id);
    const actualStart = parseDate(phase.start_date);
    const actualEnd = parseDate(phase.end_date || phase.start_date);
    const duration = Math.max(0, daysBetween(actualStart, actualEnd));

    const deps = phase.dependencies || [];
    let latestDepEnd = null;
    for (const depId of deps) {
      const depDisplay = getDisplay(depId);
      if (depDisplay && (!latestDepEnd || depDisplay.end > latestDepEnd)) {
        latestDepEnd = depDisplay.end;
      }
    }

    const displayStart = latestDepEnd && latestDepEnd > actualStart ? latestDepEnd : actualStart;
    const displayEnd = addDays(displayStart, duration);
    computing.delete(id);
    display[id] = { start: displayStart, end: displayEnd };
    return display[id];
  };

  phases.forEach(p => getDisplay(p.id));
  return display;
};

const TL_ZOOMS = [
  { key: 'day',   label: 'Day' },
  { key: 'week',  label: 'Week' },
  { key: 'month', label: 'Month' },
];

const buildTimelineLabels = (minDate, maxDate, totalDays, zoom) => {
  const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const labels = [];
  const cursor = new Date(minDate);
  let iterations = 0;

  if (zoom === 'month') {
    cursor.setDate(1);
    while (cursor <= maxDate && iterations++ < 200) {
      const pct = Math.max(0, (daysBetween(minDate, cursor) / totalDays) * 100);
      if (pct <= 100) labels.push({ pct, text: `${M[cursor.getMonth()]} ${cursor.getFullYear()}`, major: true });
      cursor.setMonth(cursor.getMonth() + 1);
    }
  } else if (zoom === 'week') {
    while (cursor <= maxDate && iterations++ < 300) {
      const pct = (daysBetween(minDate, cursor) / totalDays) * 100;
      if (pct >= 0 && pct <= 100) {
        const isMB = cursor.getDate() <= 7; // near month start
        labels.push({ pct, text: `${M[cursor.getMonth()]} ${cursor.getDate()}`, major: isMB });
      }
      cursor.setDate(cursor.getDate() + 7);
    }
  } else { // day
    while (cursor <= maxDate && iterations++ < 500) {
      const pct = (daysBetween(minDate, cursor) / totalDays) * 100;
      if (pct >= 0 && pct <= 100) {
        const isMB = cursor.getDate() === 1;
        labels.push({ pct, text: isMB ? `${M[cursor.getMonth()]} ${cursor.getDate()}` : `${cursor.getDate()}`, major: isMB });
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  return labels;
};

const STORY_DOT_COLOR = (status) => {
  const s = (status || '').toLowerCase();
  if (['done','completed','closed','resolved'].includes(s)) return '#10b981';
  if (['in_progress','in progress','active','review','testing'].includes(s)) return '#3b82f6';
  if (['blocked','cancelled','rejected'].includes(s)) return '#ef4444';
  return '#9ca3af';
};

const ExecutiveSummary = ({ phases }) => {
  const [tlZoom, setTlZoom] = React.useState('month');
  const allPhases = phases || [];
  const phaseItems = allPhases.filter(p => p.phase_type === 'phase');
  const milestoneItems = allPhases.filter(p => p.phase_type === 'milestone');
  const completed = allPhases.filter(p => p.percent_complete >= 100).length;
  const totalStories = allPhases.reduce((s, p) => s + (p.stories?.length || 0), 0);
  const doneStories = allPhases.reduce((s, p) => s + (p.stories || []).filter(st => ['done','completed','closed','resolved'].includes((st.status||'').toLowerCase())).length, 0);
  const overallProgress = allPhases.length
    ? Math.round(allPhases.reduce((s, p) => s + p.percent_complete, 0) / allPhases.length)
    : 0;
  const atRiskCount = phaseItems.filter(p => getPhaseStatus(p) === 'at-risk').length;
  const behindCount = phaseItems.filter(p => getPhaseStatus(p) === 'behind').length;
  const healthKey = behindCount > 0 ? 'behind' : atRiskCount > 0 ? 'at-risk' : 'on-track';
  const HEALTH = { 'on-track': { label: 'On Schedule', color: '#10b981' }, 'at-risk': { label: 'At Risk', color: '#f59e0b' }, 'behind': { label: 'Behind', color: '#ef4444' } };

  const logicalDates = computeLogicalDates(allPhases);
  const allDisplayDates = allPhases.flatMap(p => { const d = logicalDates[p.id]; return d ? [d.start, d.end] : []; });
  const minDate = allDisplayDates.length ? new Date(Math.min(...allDisplayDates.map(d => d.getTime()))) : new Date();
  const maxDate = allDisplayDates.length ? new Date(Math.max(...allDisplayDates.map(d => d.getTime()))) : addDays(new Date(), 90);
  const totalDays = daysBetween(minDate, maxDate) || 1;
  const lastPhase = allPhases.length ? allPhases.reduce((l, p) => { const d = logicalDates[p.id], ld = logicalDates[l.id]; return d && ld && d.end > ld.end ? p : l; }, allPhases[0]) : null;

  // Pre-compute timeline row heights to account for stories
  const BAR_H = 30;
  const STORY_H = 19;
  const PHASE_GAP = 10;
  const HEADER_H = 26;
  const rowMeta = allPhases.map(phase => {
    const stories = phase.stories || [];
    const visibleStories = Math.min(stories.length, 5);
    const extraRow = stories.length > 5 ? 1 : 0;
    const rowH = BAR_H + (stories.length > 0 ? 6 + (visibleStories + extraRow) * STORY_H : 0);
    return { phase, stories, rowH };
  });
  let yAcc = HEADER_H;
  const rowTops = rowMeta.map(({ rowH }) => { const t = yAcc; yAcc += rowH + PHASE_GAP; return t; });
  const totalChartH = yAcc;

  const STATUS_BAR_COLOR = { 'complete': '#10b981', 'on-track': null, 'at-risk': '#f59e0b', 'behind': '#ef4444', 'not-started': '#94a3b8' };

  return (
    <div className="ep-summary">

      {/* ── Compact header strip ── */}
      <div style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-3) var(--space-5)', boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', fontWeight: 500 }}>Overall Progress</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text-primary)', letterSpacing: '-0.03em', lineHeight: 1.2 }}>{overallProgress}%</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ height: 6, background: 'var(--color-bg-sunken)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${overallProgress}%`, height: '100%', background: 'var(--color-primary)', borderRadius: 3, transition: 'width 600ms ease' }} />
          </div>
        </div>
        {[
          { val: `${completed}/${phaseItems.length}`, label: 'Phases Done', color: 'var(--color-primary)' },
          { val: `${milestoneItems.filter(m => m.percent_complete >= 100).length}/${milestoneItems.length}`, label: 'Milestones', color: '#8b5cf6' },
          { val: `${doneStories}/${totalStories}`, label: 'Stories Done', color: '#10b981' },
          lastPhase ? { val: formatDateShort(toDateStr(logicalDates[lastPhase.id]?.end || parseDate(lastPhase.end_date || lastPhase.start_date))), label: 'Target Launch', color: 'var(--color-text-primary)' } : null,
          { val: HEALTH[healthKey].label, label: 'Health', color: HEALTH[healthKey].color },
        ].filter(Boolean).map((item, i) => (
          <div key={i} style={{ textAlign: 'center', flexShrink: 0, borderLeft: '1px solid var(--color-border)', paddingLeft: 'var(--space-4)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: item.color, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{item.val}</div>
            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 1, whiteSpace: 'nowrap' }}>{item.label}</div>
          </div>
        ))}
      </div>

      {/* ── Compact phase + milestone cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        {allPhases.map(phase => {
          const status = getPhaseStatus(phase);
          const stories = phase.stories || [];
          const doneC = stories.filter(s => ['done','completed','closed','resolved'].includes((s.status||'').toLowerCase())).length;
          const isMilestone = phase.phase_type === 'milestone';
          const ld = logicalDates[phase.id];
          const dStart = ld ? toDateStr(ld.start) : phase.start_date;
          const dEnd = ld ? toDateStr(ld.end) : phase.end_date;
          const barColor = STATUS_BAR_COLOR[status] || phase.color;
          return (
            <div key={phase.id} style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2) var(--space-3)', borderLeft: `3px solid ${phase.color}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4, marginBottom: 2 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1.3 }}>{isMilestone ? '◆ ' : ''}{phase.name}</div>
                <span className={`ep-status-badge ${status}`} style={{ fontSize: 9, flexShrink: 0 }}>{STATUS_LABELS[status]}</span>
              </div>
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginBottom: isMilestone ? 0 : 4 }}>
                {formatDateShort(dStart)}{!isMilestone ? ` → ${formatDateShort(dEnd)}` : ''}
                {phase.owner && <span style={{ marginLeft: 6, color: 'var(--color-text-secondary)' }}>· {phase.owner.first_name} {phase.owner.last_name?.[0]}.</span>}
              </div>
              {!isMilestone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ flex: 1, height: 3, background: 'var(--color-bg-sunken)', borderRadius: 2 }}>
                    <div style={{ width: `${phase.percent_complete}%`, height: '100%', background: barColor, borderRadius: 2 }} />
                  </div>
                  <span style={{ fontSize: 9, color: 'var(--color-text-tertiary)', minWidth: 24 }}>{phase.percent_complete}%</span>
                  {stories.length > 0 && <span style={{ fontSize: 9, color: 'var(--color-text-tertiary)' }}>{doneC}/{stories.length}</span>}
                </div>
              )}
              {isMilestone && (
                <div style={{ fontSize: 10, color: phase.percent_complete >= 100 ? '#10b981' : 'var(--color-text-tertiary)' }}>
                  {phase.percent_complete >= 100 ? '✓ Achieved' : 'Pending'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Timeline — hero section ── */}
      <div className="ep-summary-timeline">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 700 }}>Timeline Overview</h3>
            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
              Phases, milestones and linked work items · Dependencies resolved
            </div>
          </div>
          <div style={{ display: 'flex', gap: 2, background: 'var(--color-bg-sunken)', borderRadius: 'var(--radius-md)', padding: 2 }}>
            {TL_ZOOMS.map(z => (
              <button key={z.key} onClick={() => setTlZoom(z.key)} style={{ padding: '3px 10px', fontSize: 'var(--text-xs)', fontWeight: 600, border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', background: tlZoom === z.key ? 'var(--color-bg-elevated)' : 'transparent', color: tlZoom === z.key ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)', boxShadow: tlZoom === z.key ? 'var(--shadow-sm)' : 'none', transition: 'all 120ms ease' }}>
                {z.label}
              </button>
            ))}
          </div>
        </div>

        {(() => {
          const labels = buildTimelineLabels(minDate, maxDate, totalDays, tlZoom);
          const minW = tlZoom === 'day' ? Math.max(totalDays * 22, 600) : tlZoom === 'week' ? Math.max(Math.ceil(totalDays / 7) * 70, 600) : '100%';

          return (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ position: 'relative', height: totalChartH, minWidth: minW }}>

                {/* Grid lines + date labels */}
                {labels.map((lbl, i) => (
                  <React.Fragment key={i}>
                    <div style={{ position: 'absolute', left: `${lbl.pct}%`, top: 0, bottom: 0, width: 1, background: lbl.major ? 'var(--color-border)' : 'var(--color-bg-sunken)', zIndex: 0 }} />
                    <div style={{ position: 'absolute', left: `${lbl.pct}%`, top: 0, paddingLeft: 3, fontSize: lbl.major ? 10 : 9, fontWeight: lbl.major ? 700 : 400, color: lbl.major ? 'var(--color-text-secondary)' : 'var(--color-text-tertiary)', whiteSpace: 'nowrap', zIndex: 1 }}>
                      {lbl.text}
                    </div>
                  </React.Fragment>
                ))}

                {/* Phase rows */}
                {rowMeta.map(({ phase, stories }, idx) => {
                  const ld = logicalDates[phase.id];
                  const dispStart = ld ? ld.start : parseDate(phase.start_date);
                  const dispEnd = ld ? ld.end : parseDate(phase.end_date || phase.start_date);
                  const leftPct = Math.max(0, (daysBetween(minDate, dispStart) / totalDays) * 100);
                  const widthPct = phase.phase_type === 'milestone' ? 0 : Math.max(0.4, (daysBetween(dispStart, dispEnd) / totalDays) * 100);
                  const top = rowTops[idx];
                  const status = getPhaseStatus(phase);
                  const barColor = STATUS_BAR_COLOR[status] || phase.color;

                  return (
                    <React.Fragment key={phase.id}>
                      {phase.phase_type === 'milestone' ? (
                        <>
                          <div style={{ position: 'absolute', left: `${leftPct}%`, top: top + 8, width: 16, height: 16, background: phase.percent_complete >= 100 ? '#10b981' : phase.color, borderRadius: 2, transform: 'rotate(45deg)', zIndex: 2 }} />
                          <div style={{ position: 'absolute', left: `calc(${leftPct}% + 22px)`, top: top + 4, fontSize: 11, fontWeight: 600, color: 'var(--color-text-primary)', whiteSpace: 'nowrap', zIndex: 2 }}>
                            {phase.name}
                            <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 400, color: phase.percent_complete >= 100 ? '#10b981' : 'var(--color-text-tertiary)' }}>
                              {phase.percent_complete >= 100 ? '✓ Achieved' : formatDateShort(toDateStr(dispStart))}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Phase bar */}
                          <div style={{ position: 'absolute', left: `${leftPct}%`, top, width: `${widthPct}%`, height: BAR_H, borderRadius: 6, overflow: 'hidden', zIndex: 2 }}>
                            <div style={{ position: 'absolute', inset: 0, background: barColor, opacity: 0.13 }} />
                            <div style={{ position: 'absolute', top: 0, left: 0, width: `${phase.percent_complete}%`, height: '100%', background: barColor, opacity: 0.85 }} />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', paddingLeft: 8, paddingRight: 8, gap: 6 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 2px rgba(0,0,0,.35)', flex: 1, minWidth: 0 }}>{phase.name}</span>
                              <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,.9)', flexShrink: 0, textShadow: '0 1px 2px rgba(0,0,0,.35)' }}>{phase.percent_complete}%</span>
                            </div>
                          </div>

                          {/* Stories below the bar */}
                          {stories.length > 0 && (
                            <div style={{ position: 'absolute', left: `${leftPct}%`, top: top + BAR_H + 6, zIndex: 2, paddingLeft: 4 }}>
                              {stories.slice(0, 5).map((story, si) => (
                                <div key={story.id || si} style={{ display: 'flex', alignItems: 'center', gap: 5, height: STORY_H, fontSize: 10.5 }}>
                                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: STORY_DOT_COLOR(story.status), flexShrink: 0 }} />
                                  <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 600, flexShrink: 0 }}>{story.task_key}</span>
                                  <span style={{ color: 'var(--color-text-secondary)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{story.title}</span>
                                  <span style={{ color: STORY_DOT_COLOR(story.status), fontSize: 9, fontWeight: 600, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{story.status?.replace(/_/g,' ')}</span>
                                </div>
                              ))}
                              {stories.length > 5 && (
                                <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', paddingLeft: 12, height: STORY_H, display: 'flex', alignItems: 'center' }}>+{stories.length - 5} more stories</div>
                              )}
                            </div>
                          )}
                          {stories.length === 0 && (
                            <div style={{ position: 'absolute', left: `${leftPct}%`, top: top + BAR_H + 6, paddingLeft: 12, fontSize: 10, color: 'var(--color-text-tertiary)', fontStyle: 'italic', zIndex: 2 }}>No stories linked</div>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Today line */}
                {(() => {
                  const pct = (daysBetween(minDate, new Date()) / totalDays) * 100;
                  if (pct < 0 || pct > 100) return null;
                  return (
                    <div style={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, width: 2, background: '#ef4444', opacity: 0.75, borderRadius: 1, zIndex: 4 }}>
                      <div style={{ position: 'absolute', top: 2, left: '50%', transform: 'translateX(-50%)', fontSize: 8.5, fontWeight: 700, color: '#ef4444', whiteSpace: 'nowrap', letterSpacing: '0.03em' }}>TODAY</div>
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })()}

        {/* Legend */}
        <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-3)', flexWrap: 'wrap', fontSize: 10, color: 'var(--color-text-tertiary)', alignItems: 'center' }}>
          {[['#10b981','Complete'],['var(--color-primary)','On Track'],['#f59e0b','At Risk'],['#ef4444','Behind'],['#94a3b8','Not Started']].map(([c, l]) => (
            <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: c, opacity: 0.85 }} />{l}</span>
          ))}
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'var(--space-2)' }}>
            {[['#10b981','Done'],['#3b82f6','In Progress'],['#9ca3af','To Do']].map(([c,l]) => (
              <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: c, display: 'inline-block' }} />{l}</span>
            ))}
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Export Report ──────────────────────────────────────────────────────────

const generateReport = (phases, projectName, userName) => {
  const BRAND = '#2563eb';
  const now = new Date();
  const generatedStr = (() => {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const d = now;
    const hh = String(d.getHours()).padStart(2,'0');
    const mm = String(d.getMinutes()).padStart(2,'0');
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${hh}:${mm}`;
  })();
  const exportDateShort = formatDateFull(toDateStr(now));

  const allItems = phases || [];
  const phaseItems = allItems.filter(p => p.phase_type === 'phase');
  const total = allItems.length;
  const onTrackCount = phaseItems.filter(p => ['on-track', 'complete'].includes(getPhaseStatus(p))).length;
  const atRiskCount = phaseItems.filter(p => getPhaseStatus(p) === 'at-risk').length;
  const behindCount = phaseItems.filter(p => getPhaseStatus(p) === 'behind').length;
  const avgProgress = total > 0
    ? Math.round(allItems.reduce((s, p) => s + p.percent_complete, 0) / total)
    : 0;

  // Donut SVG (matches existing report)
  const circumference = 251.2;
  const filled = circumference * avgProgress / 100;
  const remaining = circumference - filled;
  const donutColor = avgProgress >= 70 ? '#10b981' : (avgProgress >= 40 ? '#f59e0b' : '#ef4444');
  const donutSvg = `<svg viewBox="0 0 100 100" width="90" height="90">
    <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" stroke-width="10"/>
    <circle cx="50" cy="50" r="40" fill="none" stroke="${donutColor}" stroke-width="10"
      stroke-dasharray="${filled.toFixed(1)} ${remaining.toFixed(1)}" stroke-dashoffset="62.8" stroke-linecap="round"/>
    <text x="50" y="54" text-anchor="middle" font-size="16" font-weight="700" fill="#111827" font-family="Helvetica Neue,Helvetica,Arial,sans-serif">${avgProgress}%</text>
  </svg>`;

  // Timeline bounds & Gantt
  const allDates = allItems.flatMap(p => [parseDate(p.start_date), parseDate(p.end_date)]);
  let minDate = allDates.length > 0 ? new Date(Math.min(...allDates.map(d => d.getTime()))) : now;
  let maxDate = allDates.length > 0 ? new Date(Math.max(...allDates.map(d => d.getTime()))) : addDays(now, 30);
  minDate = addDays(minDate, -7);
  maxDate = addDays(maxDate, 7);
  const totalSpanDays = Math.max(daysBetween(minDate, maxDate), 1);
  const chartW = 700;
  const rowH = 22;
  const headerH = 28;
  const chartH = headerH + allItems.length * rowH + 4;
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dayToX = (d) => {
    const date = typeof d === 'string' ? parseDate(d) : d;
    return Math.round((daysBetween(minDate, date) / totalSpanDays) * chartW);
  };
  const monthMarkers = [];
  const mcur = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  if (mcur < minDate) mcur.setMonth(mcur.getMonth() + 1);
  while (mcur <= maxDate) {
    const x = dayToX(mcur);
    if (x >= 0 && x <= chartW) monthMarkers.push({ x, label: `${MONTHS[mcur.getMonth()]} ${mcur.getFullYear()}` });
    mcur.setMonth(mcur.getMonth() + 1);
  }
  const todayX = dayToX(now);
  const phaseRowsSvg = allItems.map((phase, i) => {
    const y = headerH + i * rowH;
    const sx = Math.max(0, dayToX(phase.start_date));
    const ex = Math.min(chartW, dayToX(phase.end_date));
    const barW = Math.max(ex - sx, 2);
    const color = phase.color || BRAND;
    const progW = Math.round(barW * (phase.percent_complete / 100));
    if (phase.phase_type === 'milestone') {
      const cx = Math.min(Math.max(sx, 6), chartW - 6);
      const cy = y + rowH / 2;
      const sz = 6;
      return `<polygon points="${cx},${cy-sz} ${cx+sz},${cy} ${cx},${cy+sz} ${cx-sz},${cy}" fill="${color}"/>`;
    }
    return `<rect x="${sx}" y="${y+4}" width="${barW}" height="${rowH-8}" rx="2" fill="${color}" opacity="0.15"/>` +
      `<rect x="${sx}" y="${y+4}" width="${progW}" height="${rowH-8}" rx="2" fill="${color}" opacity="0.9"/>` +
      `<rect x="${sx}" y="${y+4}" width="${barW}" height="${rowH-8}" rx="2" fill="none" stroke="${color}" stroke-width="0.8" opacity="0.5"/>`;
  }).join('');
  const ganttSvg = `<svg viewBox="0 0 ${chartW} ${chartH}" width="100%" xmlns="http://www.w3.org/2000/svg" style="display:block;border:0.3pt solid #e5e7eb;background:#fff;">
    ${monthMarkers.map(m => `<line x1="${m.x}" y1="0" x2="${m.x}" y2="${chartH}" stroke="#f3f4f6" stroke-width="1"/><text x="${Math.min(m.x+3,chartW-50)}" y="18" font-size="8" fill="#9ca3af" font-family="Helvetica Neue,Helvetica,Arial,sans-serif">${m.label}</text>`).join('')}
    ${allItems.map((_, i) => i % 2 === 1 ? `<rect x="0" y="${headerH+i*rowH}" width="${chartW}" height="${rowH}" fill="#f9fafb"/>` : '').join('')}
    <line x1="0" y1="${headerH}" x2="${chartW}" y2="${headerH}" stroke="#e5e7eb" stroke-width="0.8"/>
    ${phaseRowsSvg}
    ${todayX >= 0 && todayX <= chartW ? `<line x1="${todayX}" y1="${headerH}" x2="${todayX}" y2="${chartH}" stroke="#ef4444" stroke-width="1.2" stroke-dasharray="3,2"/><circle cx="${todayX}" cy="${headerH}" r="2.5" fill="#ef4444"/><text x="${Math.min(todayX+4,chartW-28)}" y="${headerH-4}" font-size="7.5" fill="#ef4444" font-family="Helvetica Neue,Helvetica,Arial,sans-serif">Today</text>` : ''}
  </svg>`;

  // Table rows (matches existing report table style)
  const SC = {
    'complete':    { bg: '#d1fae5', color: '#065f46' },
    'on-track':   { bg: '#dbeafe', color: '#1e40af' },
    'at-risk':    { bg: '#fef3c7', color: '#92400e' },
    'behind':     { bg: '#fee2e2', color: '#991b1b' },
    'not-started':{ bg: '#f3f4f6', color: '#374151' },
  };
  const tableRows = allItems.map((phase, i) => {
    const status = phase.phase_type === 'milestone'
      ? (phase.percent_complete >= 100 ? 'complete' : 'not-started')
      : getPhaseStatus(phase);
    const sc = SC[status] || SC['not-started'];
    const duration = phase.phase_type === 'milestone' ? '—' : `${Math.max(0, daysBetween(phase.start_date, phase.end_date))}d`;
    const owner = phase.owner ? (phase.owner.display_name || phase.owner.email || '—') : '—';
    const stories = (phase.story_ids || []).length;
    const color = phase.color || BRAND;
    const alt = i % 2 === 1 ? 'background:#f9fafb;' : '';
    const typeIcon = phase.phase_type === 'milestone'
      ? `<svg width="9" height="9" viewBox="0 0 10 10" style="flex-shrink:0"><polygon points="5,0 10,5 5,10 0,5" fill="${color}"/></svg>`
      : `<span style="display:inline-block;width:9px;height:9px;background:${color};border-radius:1px;flex-shrink:0"></span>`;
    return `<tr style="${alt}">
      <td class="td"><span style="display:inline-flex;align-items:center;gap:4px;">${typeIcon}<strong style="color:#111827;">${phase.name}</strong></span></td>
      <td class="td" style="color:#6b7280;white-space:nowrap;">${phase.phase_type === 'milestone' ? '◆ Milestone' : '▬ Phase'}</td>
      <td class="td" style="white-space:nowrap;">${formatDateShort(phase.start_date)} – ${formatDateShort(phase.end_date)}</td>
      <td class="td" style="text-align:center;">${duration}</td>
      <td class="td">${owner}</td>
      <td class="td" style="min-width:90px;"><div style="display:flex;align-items:center;gap:5px;"><div style="flex:1;height:4px;background:#e5e7eb;border-radius:2px;"><div style="width:${phase.percent_complete}%;height:100%;background:${color};border-radius:2px;"></div></div><span style="font-size:7pt;color:#374151;min-width:24px;text-align:right;">${phase.percent_complete}%</span></div></td>
      <td class="td" style="text-align:center;"><span style="display:inline-block;padding:1px 6px;border-radius:10px;font-size:6.5pt;font-weight:600;background:${sc.bg};color:${sc.color};">${STATUS_LABELS[status] || status}</span></td>
      <td class="td" style="text-align:center;color:#6b7280;">${stories > 0 ? stories : '—'}</td>
    </tr>`;
  }).join('');

  // Logo URL — served from the same origin as the app
  const logoUrl = `${window.location.origin}/logo.png`;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Workstack – Executive Plan Report</title>
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
@page{size:A4;margin:12mm 14mm 16mm 14mm}
body{font-family:"Helvetica Neue",Helvetica,Arial,sans-serif;font-size:8.5pt;line-height:1.4;color:#1f2937;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}

/* Cover */
.cover-top-bar{height:8px;background:${BRAND};-webkit-print-color-adjust:exact;print-color-adjust:exact}
.cover-body{padding:32px 56px 24px;text-align:center;border-bottom:0.5pt solid #e5e7eb;margin-bottom:6mm}
.cover-logo{margin-bottom:10px}
.cover-logo img{width:40px;height:40px;object-fit:cover;border-radius:50%}
.logo-fallback{display:inline-block;width:36px;height:36px;line-height:36px;border-radius:50%;background:${BRAND};color:#fff;font-weight:700;font-size:18px;text-align:center;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.cover-divider{width:40px;height:2px;background:${BRAND};margin:0 auto 14px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.report-type-badge{display:inline-block;padding:3px 14px;border-radius:4px;font-size:7pt;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#fff;background:${BRAND};margin-bottom:10px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.cover-title{font-size:18pt;font-weight:700;color:#111827;margin:0 0 4px;letter-spacing:-.02em}
.cover-project{font-size:12pt;font-weight:400;color:${BRAND};margin:0 0 20px}
.cover-meta{width:80%;margin:0 auto;border-collapse:collapse;font-size:8pt}
.cm-label{text-transform:uppercase;letter-spacing:.05em;color:#6b7280;font-size:6.5pt;padding:3px 6px;width:25%;text-align:left;vertical-align:top}
.cm-value{font-weight:600;color:#111827;padding:3px 6px;width:25%;text-align:left;vertical-align:top}
.cover-footer{margin-top:16px;text-align:center;font-size:6.5pt;color:#9ca3af;letter-spacing:.08em;text-transform:uppercase}

/* KPI strip */
.kpi-strip{width:100%;border-collapse:collapse;margin-bottom:6mm}
.kpi-cell{text-align:center;padding:5px 4px;background:#f8fafc;border:.3pt solid #e5e7eb;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.kpi-number{font-size:18pt;font-weight:700;color:#111827;line-height:1.1}
.kpi-label{font-size:6pt;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;margin-top:2px}

/* Section */
.section-page{margin-top:5mm;page-break-inside:avoid}
.section-title{font-size:11pt;font-weight:700;color:#111827;margin:0 0 4mm;padding-bottom:2mm;border-bottom:.5pt solid ${BRAND};-webkit-print-color-adjust:exact;print-color-adjust:exact}
.section-num{color:${BRAND};margin-right:2mm;font-weight:800}
.sub-title{font-size:8.5pt;font-weight:700;color:#374151;margin:4mm 0 1.5mm;padding-bottom:.8mm;border-bottom:.25pt solid #e5e7eb}

/* Table */
.dtable{width:100%;border-collapse:collapse;margin-bottom:0;font-size:7.5pt}
.dtable th{background:${BRAND};color:#fff;font-weight:600;text-align:left;padding:2.5mm 2mm;font-size:6.5pt;text-transform:uppercase;letter-spacing:.04em;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.dtable th.c{text-align:center}
.td{padding:2mm 2mm;border-bottom:.25pt solid #e5e7eb;font-size:7.5pt;vertical-align:middle}

/* Timeline */
.legend{display:flex;gap:14px;margin-top:6px;font-size:7pt;color:#6b7280;align-items:center;flex-wrap:wrap}
.legend-item{display:flex;align-items:center;gap:4px}

/* Completion bar */
.prog-bar-bg{width:100%;height:2mm;background:#e5e7eb;border-radius:1mm;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.prog-bar-fill{height:100%;border-radius:1mm;background:${BRAND};-webkit-print-color-adjust:exact;print-color-adjust:exact}

/* Footer */
.page-footer{border-top:.5pt solid #e5e7eb;margin-top:6mm;padding-top:3mm;display:flex;justify-content:space-between;font-size:6.5pt;color:#9ca3af}
.footer-brand{font-weight:600;color:#6b7280}

/* Print */
@media print{
  .cover-top-bar,.cover-divider,.report-type-badge,.kpi-cell,.dtable th,.prog-bar-bg,.prog-bar-fill{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  thead{display:table-header-group}
  .section-page{page-break-inside:avoid}
}
</style>
</head>
<body>

<!-- ── COVER ────────────────────────────────── -->
<div class="cover-top-bar"></div>
<div class="cover-body">
  <div class="cover-logo">
    <img src="${logoUrl}" alt="Workstack" onerror="this.style.display='none';this.nextElementSibling.style.display='inline-block'"/>
    <span class="logo-fallback" style="display:none">K</span>
  </div>
  <div class="cover-divider"></div>
  <div class="report-type-badge">Executive Plan Report</div>
  <h1 class="cover-title">Executive Plan Report</h1>
  <h2 class="cover-project">${projectName}</h2>
  <table class="cover-meta">
    <tr>
      <td class="cm-label">Report Scope</td><td class="cm-value">Entire Project</td>
      <td class="cm-label">Prepared By</td><td class="cm-value">${userName || 'Workstack'}</td>
    </tr>
    <tr>
      <td class="cm-label">Items in Plan</td><td class="cm-value">${total}</td>
      <td class="cm-label">Overall Progress</td><td class="cm-value">${avgProgress}%</td>
    </tr>
    <tr>
      <td class="cm-label">Generated On</td><td class="cm-value" colspan="3">${generatedStr}</td>
    </tr>
  </table>
  <div class="cover-footer">Workstack &middot; Confidential</div>
</div>

<!-- ── SECTION 1 — AT A GLANCE ──────────────── -->
<div class="section-page">
  <div class="section-title"><span class="section-num">01</span>At a Glance</div>

  <table class="kpi-strip"><tr>
    <td class="kpi-cell" style="width:20%">
      ${donutSvg}
      <div class="kpi-label">Overall Progress</div>
    </td>
    <td class="kpi-cell"><div class="kpi-number">${total}</div><div class="kpi-label">Phases &amp; Milestones</div></td>
    <td class="kpi-cell"><div class="kpi-number" style="color:#1e40af">${onTrackCount}</div><div class="kpi-label">On Track</div></td>
    <td class="kpi-cell"><div class="kpi-number" style="color:#92400e">${atRiskCount}</div><div class="kpi-label">At Risk</div></td>
    <td class="kpi-cell"><div class="kpi-number" style="color:#991b1b">${behindCount}</div><div class="kpi-label">Behind</div></td>
  </tr></table>

  <div style="margin-bottom:2mm;">
    <div class="prog-bar-bg"><div class="prog-bar-fill" style="width:${avgProgress}%;background:${donutColor};"></div></div>
  </div>
</div>

<!-- ── SECTION 2 — TIMELINE ──────────────────── -->
${allItems.length > 0 ? `
<div class="section-page">
  <div class="section-title"><span class="section-num">02</span>Timeline Overview</div>
  ${ganttSvg}
  <div class="legend">
    <div class="legend-item"><div style="width:14px;height:7px;background:${BRAND};border-radius:1px;opacity:.85"></div>Phase progress</div>
    <div class="legend-item"><svg width="10" height="10" viewBox="0 0 10 10"><polygon points="5,0 10,5 5,10 0,5" fill="#7c3aed"/></svg>Milestone</div>
    <div class="legend-item"><div style="width:14px;border-top:1.5px dashed #ef4444"></div>Today</div>
  </div>
</div>` : ''}

<!-- ── SECTION 3 — PHASE DETAIL ──────────────── -->
<div class="section-page">
  <div class="section-title"><span class="section-num">${allItems.length > 0 ? '03' : '02'}</span>Phase &amp; Milestone Detail</div>
  <table class="dtable">
    <thead><tr>
      <th>Name</th><th>Type</th><th>Dates</th><th class="c">Duration</th>
      <th>Owner</th><th>Progress</th><th class="c">Status</th><th class="c">Stories</th>
    </tr></thead>
    <tbody>${tableRows || '<tr><td colspan="8" style="text-align:center;padding:6mm;color:#9ca3af;font-style:italic;">No phases or milestones defined yet.</td></tr>'}</tbody>
  </table>
</div>

<!-- ── FOOTER ────────────────────────────────── -->
<div class="page-footer">
  <span class="footer-brand">Workstack</span>
  <span>Generated on ${exportDateShort}</span>
</div>

<script>window.onload=function(){window.print();};</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
};

// ── Main Executive Plan Page ───────────────────────────────────────────────

const ExecutivePlan = ({ projectId }) => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const { members, currentProject } = useSelector(state => state.projects);
  const { plan, loading, saving, storySearchResults, storySearchLoading } = useSelector(state => state.executivePlan);

  const [activeView, setActiveView] = useState('planning');
  const [zoom, setZoom] = useState('months');
  const [showDeps, setShowDeps] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [phaseModal, setPhaseModal] = useState({ open: false, phase: null });
  const [storiesModal, setStoriesModal] = useState({ open: false, phase: null });

  const isViewer = user?.role === 'viewer';
  const canManagePlan = !isViewer && projectPermission(user, members, {
    projectRoles: ['admin', 'manager'],
    systemFallbackRoles: ['admin', 'manager'],
  });

  useEffect(() => {
    if (projectId) {
      dispatch(fetchExecutivePlan(projectId));
    }
    return () => { dispatch(clearPlan()); };
  }, [dispatch, projectId]);

  const phases = plan?.phases || [];
  const selectedPhase = phases.find(p => p.id === selectedId) || null;

  const handleToggleExpand = (id) => setExpandedPhases(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSavePhase = async (formData) => {
    const action = phaseModal.phase
      ? dispatch(updatePhase({ projectId, phaseId: phaseModal.phase.id, data: formData }))
      : dispatch(createPhase({ projectId, data: formData }));

    const result = await action;
    if (result.error) {
      toast.error(result.payload || 'Failed to save phase');
    } else {
      toast.success(phaseModal.phase ? 'Phase updated' : 'Phase added');
      setPhaseModal({ open: false, phase: null });
    }
  };

  const handleDeletePhase = async () => {
    if (!selectedId) return;
    if (!window.confirm('Delete this phase? This cannot be undone.')) return;
    const result = await dispatch(deletePhase({ projectId, phaseId: selectedId }));
    if (result.error) {
      toast.error(result.payload || 'Failed to delete phase');
    } else {
      toast.success('Phase deleted');
      setSelectedId(null);
    }
  };

  const handleAddStory = async (phaseId, taskId) => {
    const result = await dispatch(addStoryToPhase({ projectId, phaseId, taskId }));
    if (result.error) toast.error(result.payload || 'Failed to link story');
  };

  const handleRemoveStory = async (phaseId, taskId) => {
    const result = await dispatch(removeStoryFromPhase({ projectId, phaseId, taskId }));
    if (result.error) toast.error(result.payload || 'Failed to remove story');
  };

  const zoomIn = () => {
    const idx = ZOOM_LEVELS.findIndex(z => z.key === zoom);
    if (idx > 0) setZoom(ZOOM_LEVELS[idx - 1].key);
  };
  const zoomOut = () => {
    const idx = ZOOM_LEVELS.findIndex(z => z.key === zoom);
    if (idx < ZOOM_LEVELS.length - 1) setZoom(ZOOM_LEVELS[idx + 1].key);
  };

  const handleExport = () => {
    const projectName = currentProject?.name || 'Project';
    const userName = user?.display_name || user?.email || null;
    generateReport(phases, projectName, userName);
  };

  if (loading && !plan) {
    return (
      <div className="loading-state" style={{ flex: 1 }}>
        <span className="spinner" />
        <p>Loading executive plan…</p>
      </div>
    );
  }

  return (
    <div className="ep-page">
      {/* Toolbar */}
      <div className="ep-toolbar">
        <div className="ep-view-switcher">
          {[
            { key: 'planning', label: 'Planning', icon: <svg viewBox="0 0 16 16" fill="currentColor"><path d="M0 2a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1v7.5a2.5 2.5 0 01-2.5 2.5h-9A2.5 2.5 0 011 12.5V5a1 1 0 01-1-1V2zm2 3v7.5A1.5 1.5 0 003.5 14h9a1.5 1.5 0 001.5-1.5V5H2z"/></svg> },
            { key: 'timeline', label: 'Timeline', icon: <svg viewBox="0 0 16 16" fill="currentColor"><path d="M1 1.5a.5.5 0 01.5-.5h13a.5.5 0 010 1h-13a.5.5 0 01-.5-.5zM1 5a.5.5 0 01.5-.5h5a.5.5 0 010 1h-5A.5.5 0 011 5zm0 3.5a.5.5 0 01.5-.5h8a.5.5 0 010 1h-8a.5.5 0 01-.5-.5zm0 3.5a.5.5 0 01.5-.5h10a.5.5 0 010 1h-10a.5.5 0 01-.5-.5z"/></svg> },
            { key: 'summary', label: 'Executive Summary', icon: <svg viewBox="0 0 16 16" fill="currentColor"><path d="M4 11H2v3h2v-3zm5-4H7v7h2V7zm5-5h-2v12h2V2zm-2-1a1 1 0 00-1 1v12a1 1 0 001 1h2a1 1 0 001-1V2a1 1 0 00-1-1h-2zM6 7a1 1 0 011-1h2a1 1 0 011 1v7a1 1 0 01-1 1H7a1 1 0 01-1-1V7zM1 11a1 1 0 011-1h2a1 1 0 011 1v3a1 1 0 01-1 1H2a1 1 0 01-1-1v-3z"/></svg> },
          ].map(v => (
            <button key={v.key} className={`ep-view-btn${activeView === v.key ? ' active' : ''}`} onClick={() => setActiveView(v.key)}>
              {v.icon}{v.label}
            </button>
          ))}
        </div>

        {activeView !== 'summary' && (
          <>
            <div className="ep-toolbar-divider" />
            <div className="ep-zoom-controls">
              <button className="ep-zoom-btn" onClick={zoomIn} title="Zoom in">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path fillRule="evenodd" d="M6.5 12a5.5 5.5 0 100-11 5.5 5.5 0 000 11zM13 6.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"/><path d="M10.344 11.742c.03.04.062.078.098.115l3.85 3.85a1 1 0 001.415-1.414l-3.85-3.85a1.007 1.007 0 00-.115-.1 6.538 6.538 0 01-1.398 1.4z"/><path fillRule="evenodd" d="M3 6.5a.5.5 0 01.5-.5h6a.5.5 0 010 1h-6a.5.5 0 01-.5-.5z"/></svg>
              </button>
              <span className="ep-zoom-label">{ZOOM_LEVELS.find(z => z.key === zoom)?.label}</span>
              <button className="ep-zoom-btn" onClick={zoomOut} title="Zoom out">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path fillRule="evenodd" d="M6.5 12a5.5 5.5 0 100-11 5.5 5.5 0 000 11zM13 6.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"/><path d="M10.344 11.742c.03.04.062.078.098.115l3.85 3.85a1 1 0 001.415-1.414l-3.85-3.85a1.007 1.007 0 00-.115-.1 6.538 6.538 0 01-1.398 1.4z"/><path fillRule="evenodd" d="M6.5 3a.5.5 0 01.5.5V6h2.5a.5.5 0 010 1H7v2.5a.5.5 0 01-1 0V7H3.5a.5.5 0 010-1H6V3.5a.5.5 0 01.5-.5z"/></svg>
              </button>
            </div>
            <div className="ep-toolbar-divider" />
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showDeps} onChange={e => setShowDeps(e.target.checked)} style={{ width: 14, height: 14 }} />
              Dependencies
            </label>
          </>
        )}

        <div className="ep-toolbar-spacer" />

        {/* Export button — always visible */}
        <button
          className="ep-btn ep-btn-secondary ep-btn-sm"
          onClick={handleExport}
          title="Export as PDF report"
          disabled={phases.length === 0}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z"/>
            <path d="M7.646 11.854a.5.5 0 00.708 0l3-3a.5.5 0 00-.708-.708L8.5 10.293V1.5a.5.5 0 00-1 0v8.793L5.354 8.146a.5.5 0 10-.708.708l3 3z"/>
          </svg>
          Export PDF
        </button>

        {/* Header action buttons */}
        {canManagePlan && (
          <div className="ep-header-actions">
            {selectedPhase && (
              <>
                <button
                  className="ep-btn ep-btn-secondary ep-btn-sm"
                  onClick={() => setStoriesModal({ open: true, phase: selectedPhase })}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M14 1a1 1 0 011 1v12a1 1 0 01-1 1H2a1 1 0 01-1-1V2a1 1 0 011-1h12zM2 0a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V2a2 2 0 00-2-2H2z"/><path d="M8 4a.5.5 0 01.5.5v3h3a.5.5 0 010 1h-3v3a.5.5 0 01-1 0v-3h-3a.5.5 0 010-1h3v-3A.5.5 0 018 4z"/></svg>
                  Link Stories
                </button>
                <button
                  className="ep-btn ep-btn-ghost ep-btn-sm"
                  style={{ color: '#ef4444' }}
                  onClick={handleDeletePhase}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"/><path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H5.5l1-1h3l1 1H14a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" clipRule="evenodd"/></svg>
                  Delete
                </button>
              </>
            )}
            <button
              className="ep-btn ep-btn-primary"
              onClick={() => setPhaseModal({ open: true, phase: null })}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 4a.5.5 0 01.5.5v3h3a.5.5 0 010 1h-3v3a.5.5 0 01-1 0v-3h-3a.5.5 0 010-1h3v-3A.5.5 0 018 4z"/></svg>
              Add Phase
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <SummaryStats phases={phases} />

      {/* Content */}
      {activeView === 'planning' && (
        <div className="ep-split">
          <PlanTable
            phases={phases}
            expandedPhases={expandedPhases}
            onToggleExpand={handleToggleExpand}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onEdit={phase => canManagePlan && setPhaseModal({ open: true, phase })}
          />
          <GanttChart
            phases={phases}
            expandedPhases={expandedPhases}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onEdit={phase => canManagePlan && setPhaseModal({ open: true, phase })}
            zoom={zoom}
            showDeps={showDeps}
          />
        </div>
      )}

      {activeView === 'timeline' && (
        <div className="ep-split" style={{ '--ep-table-width': '0px' }}>
          <GanttChart
            phases={phases}
            expandedPhases={expandedPhases}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onEdit={phase => canManagePlan && setPhaseModal({ open: true, phase })}
            zoom={zoom}
            showDeps={showDeps}
          />
        </div>
      )}

      {activeView === 'summary' && <ExecutiveSummary phases={phases} />}

      {/* Modals */}
      <PhaseModal
        isOpen={phaseModal.open}
        onClose={() => setPhaseModal({ open: false, phase: null })}
        onSave={handleSavePhase}
        phase={phaseModal.phase}
        allPhases={phases}
        members={members}
        saving={saving}
      />
      <StoriesModal
        isOpen={storiesModal.open}
        onClose={() => setStoriesModal({ open: false, phase: null })}
        phase={storiesModal.phase ? phases.find(p => p.id === storiesModal.phase.id) : null}
        projectId={projectId}
        onAdd={handleAddStory}
        onRemove={handleRemoveStory}
        searchResults={storySearchResults}
        searchLoading={storySearchLoading}
      />
    </div>
  );
};

export default ExecutivePlan;

